import {
  Component,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  ChangeDetectorRef,
  input,
  output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzDropdownModule } from 'ng-zorro-antd/dropdown';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzSegmentedModule } from 'ng-zorro-antd/segmented';
import {
  MessageApiService,
  Message,
  MessageThreadOpenedEvent,
  MessageThreadQuery,
  TaskUnreadClearedEvent,
  TaskStatisItem,
  MessageType,
  ThreadMessageFilter,
} from '../../message.api.service';
import { MessageListComponent } from '../message-list/message-list.component';
import { MessageItemKindPipe } from './task-message-question-nos.component';
import { ReplyComposerComponent, ReplyTarget, ReplySendPayload } from '../reply/reply-composer.component';
import { TaskStatisStore } from './task-statis.store';
import { FlIconComponent } from '@common';

/**
 * 任务消息视图（对 message-list 的任务相关加强）
 *
 * 领域模型见 message/README.md：
 * - 输入 taskId，调 statis 拉该任务的题号列表（得分率/未读/thread_id），构建右侧题号导航轨 + 当前题概览条
 * - 默认/自动选中第一题，切题时把 thread_id + taskId + moduleId + paperQuestionId 全量传给内部 fl-message-list
 * - 概览进详情 → 跳任务分析页 task-analysis
 * - 回复：点学生头像 → 唤起底部回复器（预填引用）；点底部提示条 → 回复全班；
 *   发送后按目标调用 replyMessage / createMessage，成功后刷新消息流
 */
@Component({
  selector: 'fl-task-message-list',
  templateUrl: './task-message-list.component.html',
  styleUrls: ['./task-message-list.component.scss'],
  standalone: true,
  imports: [
    NzSpinModule,
    NzButtonModule,
    NzIconModule,
    NzDropdownModule,
    NzMenuModule,
    NzPopconfirmModule,
    NzSegmentedModule,
    FormsModule,
    MessageListComponent,
    MessageItemKindPipe,
    ReplyComposerComponent,
    FlIconComponent,
  ],
})
export class TaskMessageListComponent implements OnChanges, OnInit, OnDestroy {
  /** 任务 id */
  readonly taskId = input<number>(undefined);

  /** 当前任务某个叶子 thread 已读，供外层任务聚合数字同步扣减 */
  readonly unreadCleared = output<TaskUnreadClearedEvent>();

  /** 当前任务 statis（读自共享 store，左栏生产、右栏消费，避免重复请求） */
  get statis(): TaskStatisItem[] {
    return this.statisStore.get(this.taskId() ?? -1);
  }

  /** 当前选中题的索引 */
  selectedIndex = 0;

  /** 外部（左栏手风琴）控制的选中题 thread_id（TaskStatisItem.id）；未指定时默认第一题 */
  // TODO: Skipped for migration because:
  //  Your application code writes to the input. This prevents migration.
  @Input() externalThreadId?: number;

  /** 传给内层 message-list 的查询描述（切题/换任务时重建一次） */
  threadQuery?: MessageThreadQuery;

  /** 消息流筛选：全部 / 未回复 */
  messageFilter: ThreadMessageFilter = ThreadMessageFilter.All;

  /** 供模板引用筛选枚举 */
  protected readonly ThreadMessageFilter = ThreadMessageFilter;

  /** 当前题消息流（供概览条聚合标签） */
  currentMessages: Message[] = [];

  /** 加载中 */
  loading = false;

  /** 回复器目标：null 不显示；有 msgId 回复某学生，无则回复全班 */
  replyTarget: ReplyTarget | null = null;

  /** 清空信号：每次 onCancelReply（切题/关闭/发送后）递增，驱动回复器清空输入 */
  clearCount = 0;

  /** 「同步全班」勾选状态（通过 [toAll] 插槽由宿主自持） */
  toAll = false;

  /** 回复成功后递增，触发 message-list 重拉 */
  private refreshCount = 0;

  /** 内层消息流引用（批量删除时切换到选择模式） */
  // TODO: Skipped for migration because:
  //  This query is used in a control flow expression (e.g. `@if` or `*ngIf`)
  //  and migrating would break narrowing currently.
  @ViewChild('messageList') messageList?: MessageListComponent;

  private messageApiSrv = inject(MessageApiService);
  private statisStore = inject(TaskStatisStore);
  private router = inject(Router);
  private messageSrv = inject(NzMessageService);
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  /** store 变更订阅句柄（初始化为 null，ngOnInit 建立，ngOnDestroy 取消） */
  private statSub: Subscription | null = null;

  /** 上次已应用初始化的 statis 数组引用：用于防 BehaviorSubject 回放导致同一份数据被重复初始化 */
  private appliedStatisRef: TaskStatisItem[] | null = null;

  /** 当前选中的题（statis 项） */
  get currentItem(): TaskStatisItem | undefined {
    return this.statis[this.selectedIndex];
  }

  /** 传给 message-list 的刷新信号（每次回复成功 +1） */
  get refreshTick(): number {
    return this.refreshCount;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['taskId']?.currentValue) {
      // 切换任务（左侧消息聚合列表）时关闭上一任务的回复器，并应用共享 store 的 statis
      this.onCancelReply();
      // 新任务默认回到「全部」，避免沿用上个任务的「未回复」筛选导致详情看似为空
      this.messageFilter = ThreadMessageFilter.All;
      this.loading = true;
      this.applyStatis();
    } else if (changes['externalThreadId'] && this.externalThreadId != null) {
      // 左栏手风琴点了某题号：按 thread_id 定位并切换到对应题
      this.selectThreadById(this.externalThreadId);
    }
  }

  ngOnInit() {
    // 订阅共享 store：左栏写入本任务 statis（load）时完成右栏初始化；
    // 未读扣减（patch）由 statis getter 自动反映，无需重初始化
    this.statSub = this.statisStore
      .changes()
      .pipe(filter(c => c !== null && c.taskId === this.taskId()))
      .subscribe(c => {
        if (c!.type === 'load') {
          this.applyStatis(true);
          // OnPush：共享 store 广播回调非模板事件，statis 初始化结果需主动标记
          this.cdr.markForCheck();
        }
      });
  }

  ngOnDestroy() {
    this.statSub?.unsubscribe();
    this.statSub = null;
  }

  /** 应用共享 store 中的当前任务 statis：复位并按当前题重建查询/定位，通知内层消息流重拉 */
  private applyStatis(fromBroadcast = false) {
    const taskId = this.taskId();
    if (!taskId) return;
    const list = this.statisStore.get(taskId);
    if (list.length === 0) {
      // 左栏已拉完（0 题任务或拉取失败）仍应结束 spinner 显示空态；首次切任务（左栏拉取中）则保持 loading 等广播
      if (fromBroadcast) this.loading = false;
      this.appliedStatisRef = null;
      return;
    }
    // 同一份数据引用（如 BehaviorSubject 回放已应用过的 load）不再重复初始化，避免 refreshCount 双计数、消息流双拉
    if (list === this.appliedStatisRef) return;
    this.appliedStatisRef = list;
    this.selectedIndex = 0;
    this.threadQuery = undefined;
    this.currentMessages = [];
    this.buildThreadQuery();
    // statis 就绪后，若左栏已指定某题，则按 thread_id 应用外部选中的题
    if (this.externalThreadId != null) {
      this.selectThreadById(this.externalThreadId);
    }
    // 递增内层消息流刷新信号，确保当前题消息流也重拉
    this.refreshCount += 1;
    this.loading = false;
  }

  /** 按 thread_id 在当前 statis 中定位并选中该题（未命中则保持第一题） */
  private selectThreadById(threadId: number) {
    const index = this.statis.findIndex(s => s.id === threadId);
    if (index >= 0) {
      this.onSelectQuestion(index);
    }
  }

  /** 按当前选中题重建 threadQuery（全量携带定位参数） */
  private buildThreadQuery() {
    const item = this.currentItem;
    this.threadQuery = item
      ? {
          threadId: item.id,
          taskId: this.taskId(),
          paperQuestionId: item.paperQuestionId,
          moduleId: item.moduleId,
        }
      : undefined;
  }

  /** 切换题号 */
  onSelectQuestion(index: number) {
    if (index === this.selectedIndex) return;
    this.selectedIndex = index;
    this.buildThreadQuery();
    // 切题后清空回复目标，避免把上题的回复发到别题
    this.onCancelReply();
  }

  /** 切换消息流筛选：同时收起回复器，避免引用目标在筛选后已不可见 */
  onMessageFilterChange(filter: ThreadMessageFilter) {
    this.messageFilter = filter;
    this.onCancelReply();
  }

  /** 进详情 → 任务分析页（携带当前题 questionId，定位到对应题目，而非默认第一题）；始终开新 tab */
  goDetail() {
    const taskId = this.taskId();
    if (!taskId) return;
    const questionId = this.currentItem?.questionId;
    const url = this.router.serializeUrl(
      this.router.createUrlTree([`/task/${taskId}/analysis`], {
        queryParams: questionId != null ? { questionId } : undefined,
      })
    );
    window.open(url, '_blank');
  }

  /** 概览条「批量删除」：切换到内层消息流批量选择模式（删除动作与接口后续补） */
  onBatchDelete() {
    this.messageList?.enterSelectionMode();
  }

  /** 批量删除确认弹窗标题（含选中条数，模板 nz-popconfirm 用） */
  get deleteTitle(): string {
    const count = this.messageList?.selectedIds.size ?? 0;
    return `确定删除选中的 ${count} 条消息吗？删除后不可恢复。`;
  }

  /** 底部批量条「删除」：调后端批量删除接口，成功后退选择模式并刷新当前题消息流（二次确认由模板 nz-popconfirm 承担） */
  async deleteSelection() {
    const ids = [...(this.messageList?.selectedIds ?? [])];
    if (!ids.length) return;
    try {
      await this.messageApiSrv.deleteMessages(ids);
      this.messageSrv.success('删除成功');
      this.messageList?.exitSelectionMode();
      this.refreshCount += 1;
      // OnPush：删除 await 续体非模板事件，刷新信号需推送至内层消息流
      this.cdr.markForCheck();
    } catch (e: any) {
      this.messageSrv.error(e?.error?.detail ?? '删除失败，请重试');
    }
  }

  /** message-list 回传当前消息流（供概览条聚合标签） */
  onMessagesChanged(messages: Message[]) {
    this.currentMessages = messages;
  }

  /** thread 首屏获取成功：只清除当前题目/模块/任务反馈的未读 */
  onThreadOpened(event: MessageThreadOpenedEvent) {
    const item = this.statis.find(statis => statis.id === event.query.threadId);
    const clearedCount = Math.max(0, item?.unReadCount ?? 0);
    const taskId = this.taskId();
    if (!item || clearedCount === 0 || !taskId) return;

    // 扣减写入共享 store（statis getter 自动反映）；左栏经 unreadCleared 事件同步其手风琴副本
    this.statisStore.patchUnread(taskId, event.query.threadId, clearedCount);
    this.messageApiSrv.consumeMessageUnread(clearedCount);
    this.unreadCleared.emit({
      taskId: taskId,
      threadId: event.query.threadId,
      clearedCount,
    });
  }

  /** 点学生消息头像 → 唤起回复器，预填该生引用目标 */
  onAvatarClick(message: Message) {
    this.replyTarget = {
      msgId: message.id,
      firstName: message.firstName,
      content: message.content,
      tags: message.tags,
      messageType: message.messageType,
      mediaUrls: message.mediaUrls,
      avatar: message.avatar,
    };
    this.toAll = false;
  }

  /** 点底部提示条 → 唤起回复器（无 msgId，回复全班） */
  onOpenClassReply() {
    this.replyTarget = {};
    this.toAll = false;
  }

  /** 关闭回复器 */
  onCancelReply() {
    this.replyTarget = null;
    this.toAll = false;
    // 递增清空信号：常驻「回复全班」场景下 replyTarget 本就不变，需靠此信号驱动回复器清空输入
    this.clearCount += 1;
  }

  /** 点「全班可见」勾选：回复全班时它只作提示（消息本就全班可见），吞掉点击避免勾选框被切换 */
  onToAllClick(event: MouseEvent) {
    if (!this.replyTarget?.msgId) event.preventDefault();
  }

  /** 发送回复：有 msgId 走 replyMessage（toAll 控制是否同步全班），无则 createMessage 全班 */
  async onSendReply(payload: ReplySendPayload) {
    const target = this.replyTarget;
    if (!payload.content && payload.mediaUrls.length === 0) return;
    // 回复全班需按当前题定位，题未加载时不发送（带题号/任务上下文）
    if (!target?.msgId && !this.threadQuery) return;
    try {
      if (target?.msgId) {
        await this.messageApiSrv.replyMessage(target.msgId, {
          content: payload.content,
          toAll: this.toAll,
          mediaUrls: payload.mediaUrls,
        });
      } else {
        await this.messageApiSrv.createMessage({
          taskId: this.taskId(),
          paperQuestionId: this.threadQuery?.paperQuestionId,
          moduleId: this.threadQuery?.moduleId ?? null,
          content: payload.content,
          mediaUrls: payload.mediaUrls,
          type: MessageType.Default,
        });
      }
      this.refreshCount += 1;
      this.onCancelReply();
      // OnPush：发送 await 续体非模板事件，刷新信号/回复器复位需主动标记
      this.cdr.markForCheck();
    } catch (e: any) {
      // 后端业务错误（如「反馈修改已关闭」）取 e.error.detail，否则兜底提示
      this.messageSrv.error(e?.error?.detail ?? '发送失败，请重试');
    }
  }
}
